import { Button, Center, Flex, Group, Loader, Pagination, Paper, Select, Stack, Text, Title, useMantineTheme } from "@mantine/core";
import DateRangePicker from "./DateRangePicker";
import { FaSearch } from "react-icons/fa";
import { useEffect, useState } from "react";
import { convertLocalDateStrToUtc, convertToLocalDateTime } from "../utilities/DateUtil";
import { PushNotificationRecord } from "../models/PushNotificationRecord";
import { searchPushNotificationRecords } from "../services/PushNotificationRecordService";

interface PushNotificationRecordsTabProps {
  setSelectedRecord: (selectedRecord: PushNotificationRecord) => void;
  openDrawer: () => void;
}

export const PushNotificationRecordsTab = ({
  setSelectedRecord,
  openDrawer
}: PushNotificationRecordsTabProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [pushNotificationRecords, setPushNotificationRecords] = useState<PushNotificationRecord[]>([]);
  const [searched, setSearched] = useState<boolean>(false);
  const [recordsPageNum, setRecordsPageNum] = useState<number>(1);
  const [recordsTotalPages, setRecordsTotalPages] = useState<number>(0);
  const [recordsPageSize, setRecordsPageSize] = useState<number>(20);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const theme = useMantineTheme();

  const onDateRangeSelected = (startDate?: string, endDate?: string) => {
    setStartDate(startDate ? convertLocalDateStrToUtc(startDate) : null);
    setEndDate(endDate ? convertLocalDateStrToUtc(endDate, true) : null);
  };

  const onRecordsSearch = async () => {
    if (startDate && endDate) {
      setLoading(true);

      try {
        const records = await searchPushNotificationRecords(
          recordsPageNum,
          startDate,
          endDate,
          setRecordsTotalPages,
          setTotalRecords,
          setLoading,
          recordsPageSize
        );
        setPushNotificationRecords(records);
        if (!searched) {
          setSearched(true);
        }
      } catch (error) {
        console.error("Error fetching push notification records:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const truncate = (text: string, maxLength: number) => {
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  };

  useEffect(() => {
    if (startDate && endDate && searched) {
      onRecordsSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordsPageNum, recordsPageSize]);

  return (
    <Flex direction="column" w="100%">
      <Paper shadow="xl" p="md" my="md">
        <Group justify="center">
          <DateRangePicker onChange={onDateRangeSelected} />
          <Button
            onClick={onRecordsSearch}
            size="md"
            disabled={!startDate || !endDate}
            leftSection={<FaSearch className="search-icon" />}
            title={
              !startDate || !endDate
                ? "Select both a start date and an end date to enable searching."
                : ""
            }
          >
            Search
          </Button>
        </Group>
      </Paper>

      {startDate && endDate && searched && (
        <Paper p="md" shadow="xl">
          <Group justify="center" gap="md">
            <Title order={4}>Page</Title>

            <Pagination
              total={recordsTotalPages}
              value={recordsPageNum}
              onChange={setRecordsPageNum}
              withEdges
            />
            <Text>
              Showing {(recordsPageNum - 1) * recordsPageSize + 1}-
              {Math.min(
                (recordsPageNum - 1) * recordsPageSize + pushNotificationRecords.length, totalRecords
              )} of {totalRecords} results | Show
            </Text>
            <Select
              value={String(recordsPageSize)}
              onChange={(value) => {
                if (value !== null) {
                  const newPageSize = Number(value);
                  const totalRecords = pushNotificationRecords.length;
                  const maxPageAfterChange = Math.ceil(totalRecords / newPageSize);

                  if (recordsPageNum <= maxPageAfterChange) {
                    setRecordsPageSize(newPageSize);
                  }
                }
              }}
              data={['20', '50', '100', '200']}
              w={80}
            />
            <Text>
              per page
            </Text>
          </Group>
        </Paper>
      )}

      <Paper p="md" shadow="xl" mb="md">
        <Group
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 0.5fr 0.5fr 1fr",
          }}
        >
          <Text span fw={700}>
            Title
          </Text>
          <Text span fw={700}>
            Body
          </Text>
          <Text span fw={700}>
            Event Type
          </Text>
          <Text span fw={700}>
            Link
          </Text>
          <Text span fw={700}>
            Sender
          </Text>
          <Text span fw={700}>
            Device Attempts
          </Text>
          <Text span fw={700}>
            Device Successes
          </Text>
          <Text span fw={700}>
            Time Sent
          </Text>
        </Group>
      </Paper>

      {loading ? (
        <Center mt="xl">
          <Loader size="xl" />
        </Center>
      ) : (
        <Stack gap="xs">
          {startDate && endDate && searched ? (
            pushNotificationRecords.map((pushNotificationRecord, index) => (
              <Paper
                key={index}
                p="md"
                shadow="xl"
                style={{
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setSelectedRecord(pushNotificationRecord);
                  openDrawer();
                }}
              >
                <Group
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 0.5fr 0.5fr 1fr",
                  }}
                >
                  <Text span>{truncate(pushNotificationRecord.title, 30)}</Text>
                  <Text span>{truncate(pushNotificationRecord.body ?? '', 30)}</Text>
                  <Text span>{pushNotificationRecord.eventType}</Text>
                  <Text span>{truncate(pushNotificationRecord.link ?? '', 30)}</Text>
                  <Text span>{pushNotificationRecord.adminUserName}</Text>
                  <Text span>{pushNotificationRecord.deviceAttempts}</Text>
                  <Text span>{pushNotificationRecord.deviceSuccesses}</Text>
                  <Text span>{convertToLocalDateTime(pushNotificationRecord.sendTime)}</Text>
                </Group>
              </Paper>
            ))
          ) : (
            <Center mt="lg">
              <Text c={theme.colors.gray[7]}>
                Set date range to begin search!
              </Text>
            </Center>
          )}
        </Stack>
      )}
    </Flex>
  );
}
